import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../mutation-types';
import CampaignsAPI from '../../api/campaigns';
import AnalyticsHelper from '../../helper/AnalyticsHelper';
import { CAMPAIGNS_EVENTS } from '../../helper/AnalyticsHelper/events';
import { CAMPAIGN_TYPES } from 'shared/constants/campaign';
import { INBOX_TYPES } from 'dashboard/helper/inbox';

const toTimestamp = campaign => {
  const timestamp = Date.parse(campaign.created_at);
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const sortByNewest = (first, second) => {
  const createdAtDifference = toTimestamp(second) - toTimestamp(first);
  if (createdAtDifference !== 0) return createdAtDifference;

  return (second.id || 0) - (first.id || 0);
};

export const state = {
  records: [],
  uiFlags: {
    isFetching: false,
    isCreating: false,
  },
};

export const getters = {
  getUIFlags(_state) {
    return _state.uiFlags;
  },
  getCampaigns:
    _state =>
    (campaignType, inboxChannelTypes = null) => {
      let filteredRecords = _state.records.filter(
        record => record.campaign_type === campaignType
      );

      if (inboxChannelTypes && Array.isArray(inboxChannelTypes)) {
        filteredRecords = filteredRecords.filter(record => {
          return (
            record.inbox &&
            inboxChannelTypes.includes(record.inbox.channel_type)
          );
        });
      }

      return filteredRecords.sort(sortByNewest);
    },
  getSMSCampaigns: (_state, _getters) => {
    const smsChannelTypes = [INBOX_TYPES.SMS, INBOX_TYPES.TWILIO];
    return _getters.getCampaigns(CAMPAIGN_TYPES.ONE_OFF, smsChannelTypes);
  },
  getWhatsAppCampaigns: (_state, _getters) => {
    return _getters.getCampaigns(CAMPAIGN_TYPES.ONE_OFF).filter(record => {
      const channelType = record.inbox?.channel_type;
      const isWhatsAppInbox = channelType === INBOX_TYPES.WHATSAPP;
      const isEvolutionApiInbox =
        channelType === INBOX_TYPES.API &&
        record.inbox?.additional_attributes?.is_evolution_api_integration;

      return isWhatsAppInbox || isEvolutionApiInbox;
    });
  },
  getLiveChatCampaigns: (_state, _getters) => {
    const liveChatChannelTypes = [INBOX_TYPES.WEB];
    return _getters.getCampaigns(CAMPAIGN_TYPES.ONGOING, liveChatChannelTypes);
  },
  getAllCampaigns: _state => {
    return [..._state.records].sort(sortByNewest);
  },
};

export const actions = {
  get: async function getCampaigns({ commit }, options = {}) {
    const { silent = false } = options;
    if (!silent) {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isFetching: true });
    }
    try {
      const response = await CampaignsAPI.get();
      commit(types.SET_CAMPAIGNS, response.data);
    } catch (error) {
      // Ignore error
    } finally {
      if (!silent) {
        commit(types.SET_CAMPAIGN_UI_FLAG, { isFetching: false });
      }
    }
  },
  create: async function createCampaign({ commit }, campaignObj) {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isCreating: true });
    try {
      const response = await CampaignsAPI.create(campaignObj);
      commit(types.ADD_CAMPAIGN, response.data);
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isCreating: false });
    }
  },
  update: async ({ commit }, { id, ...updateObj }) => {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isUpdating: true });
    try {
      const response = await CampaignsAPI.update(id, updateObj);
      AnalyticsHelper.track(CAMPAIGNS_EVENTS.UPDATE_CAMPAIGN);
      commit(types.EDIT_CAMPAIGN, response.data);
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isUpdating: false });
    }
  },
  delete: async ({ commit }, id) => {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isDeleting: true });
    try {
      const response = await CampaignsAPI.delete(id);
      AnalyticsHelper.track(CAMPAIGNS_EVENTS.DELETE_CAMPAIGN);
      commit(types.EDIT_CAMPAIGN, response.data);
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isDeleting: false });
    }
  },
  hardDelete: async ({ commit }, id) => {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isDeleting: true });
    try {
      await CampaignsAPI.hardDelete(id);
      AnalyticsHelper.track(CAMPAIGNS_EVENTS.DELETE_CAMPAIGN);
      commit(types.DELETE_CAMPAIGN, id);
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isDeleting: false });
    }
  },
  reprocess: async ({ commit, state: moduleState }, id) => {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isUpdating: true });
    const campaign = moduleState.records.find(record => record.id === id);
    const previousCampaign = campaign ? { ...campaign } : null;

    if (campaign) {
      commit(types.EDIT_CAMPAIGN, {
        ...campaign,
        campaign_status: 'active',
      });
    }

    try {
      const response = await CampaignsAPI.reprocess(id);
      commit(types.EDIT_CAMPAIGN, response.data);
    } catch (error) {
      if (previousCampaign) {
        commit(types.EDIT_CAMPAIGN, previousCampaign);
      }
      throw error;
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isUpdating: false });
    }
  },
  pause: async ({ commit }, id) => {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isUpdating: true });
    try {
      const response = await CampaignsAPI.pause(id);
      commit(types.EDIT_CAMPAIGN, response.data);
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isUpdating: false });
    }
  },
  resume: async ({ commit }, id) => {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isUpdating: true });
    try {
      const response = await CampaignsAPI.resume(id);
      commit(types.EDIT_CAMPAIGN, response.data);
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isUpdating: false });
    }
  },
};

export const mutations = {
  [types.SET_CAMPAIGN_UI_FLAG](_state, data) {
    _state.uiFlags = {
      ..._state.uiFlags,
      ...data,
    };
  },

  [types.ADD_CAMPAIGN]: MutationHelpers.create,
  [types.SET_CAMPAIGNS]: MutationHelpers.set,
  [types.EDIT_CAMPAIGN]: MutationHelpers.update,
  [types.DELETE_CAMPAIGN]: MutationHelpers.destroy,
};

export default {
  namespaced: true,
  actions,
  state,
  getters,
  mutations,
};
